import React, { useState } from 'react';
import { X, AlertTriangle, Calendar, Video } from 'lucide-react';
import type { SalesActivity } from '../../lib/types';

interface DeleteActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (deleteCalendarEvent: boolean) => Promise<void>;
  activity: SalesActivity | null;
  isDeleting?: boolean;
}

export function DeleteActivityModal({
  isOpen,
  onClose,
  onConfirm,
  activity,
  isDeleting = false
}: DeleteActivityModalProps) {
  const [deleteCalendarEvent, setDeleteCalendarEvent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !activity) return null;

  const hasGoogleEvent = Boolean(activity.google_event_id);
  const showCalendarOption = hasGoogleEvent;

  // Get activity type label for better UX
  const getActivityTypeLabel = () => {
    switch (activity.type) {
      case 'videochiamata': return 'videochiamata';
      case 'appuntamento': return 'appuntamento fisico';
      case 'sopralluogo': return 'sopralluogo';
      case 'call': return 'chiamata';
      case 'task': return 'task';
      case 'email': return 'email';
      default: return 'attività';
    }
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(deleteCalendarEvent);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Elimina {getActivityTypeLabel()}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting || isDeleting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Activity info */}
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <p className="text-sm font-medium text-gray-900">{activity.title}</p>
            {activity.meet_link && (
              <div className="flex items-center gap-1 mt-1 text-xs text-blue-600">
                <Video className="h-3 w-3" />
                <span>Con link Google Meet</span>
              </div>
            )}
          </div>

          {/* Warning message */}
          <p className="text-sm text-gray-600">
            {showCalendarOption
              ? `Questa ${getActivityTypeLabel()} ha un evento Google Calendar associato.`
              : 'Sei sicuro di voler eliminare questa attività? Questa azione non può essere annullata.'
            }
          </p>

          {/* Calendar deletion option */}
          {showCalendarOption && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteCalendarEvent}
                  onChange={(e) => setDeleteCalendarEvent(e.target.checked)}
                  disabled={isSubmitting || isDeleting}
                  className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span>Cancella anche l'evento Google Calendar</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {activity.meet_link
                      ? 'Eliminerà il Meet e invierà notifiche di cancellazione a tutti i partecipanti'
                      : 'Invierà notifiche di cancellazione a tutti i partecipanti'
                    }
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Info if NOT deleting calendar */}
          {showCalendarOption && !deleteCalendarEvent && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                <strong>Nota:</strong> L'evento Google Calendar e il link Meet rimarranno attivi.
                I partecipanti non riceveranno notifiche di cancellazione.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSubmitting || isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {(isSubmitting || isDeleting) && (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            )}
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}
